import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import { useEtfFlows } from '../hooks/useEtfFlows';
import { trackFilterChange, trackFeatureUsage } from '../utils/analytics';
import './EtfFlowPage.css';

function EtfFlowPage() {
  const { t } = useTranslation();
  const { data, loading, error, today, history, formatFlow } = useEtfFlows();
  const [selectedDate, setSelectedDate] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Get last 8 days for date selector
  const dateOptions = useMemo(() => {
    if (!history || history.length === 0) return [];
    return history.slice(0, 8);
  }, [history]);

  // Set default selected date to most recent
  useEffect(() => {
    if (dateOptions.length > 0 && !selectedDate) {
      setSelectedDate(dateOptions[0].date);
    }
  }, [dateOptions, selectedDate]);

  // Get data for selected date
  const selectedData = useMemo(() => {
    if (!selectedDate || !history) return null;
    return history.find(h => h.date === selectedDate);
  }, [selectedDate, history]);

  // Calculate bar heights (relative to max value)
  const barHeights = useMemo(() => {
    if (!selectedData) return { BTC: 0, ETH: 0, SOL: 0 };

    const values = [
      Math.abs(selectedData.BTC || 0),
      Math.abs(selectedData.ETH || 0),
      Math.abs(selectedData.SOL || 0)
    ];
    const maxValue = Math.max(...values, 1); // Avoid division by zero

    return {
      BTC: Math.max((Math.abs(selectedData.BTC || 0) / maxValue) * 100, 5),
      ETH: Math.max((Math.abs(selectedData.ETH || 0) / maxValue) * 100, 5),
      SOL: Math.max((Math.abs(selectedData.SOL || 0) / maxValue) * 100, 5)
    };
  }, [selectedData]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Format full date
  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Check if value is positive
  const isPositive = (value) => value !== null && value >= 0;

  // FAQ data - using translations
  const faqData = useMemo(() => [
    {
      question: t('etfFlows.faq.q1'),
      answer: t('etfFlows.faq.a1')
    },
    {
      question: t('etfFlows.faq.q2'),
      answer: t('etfFlows.faq.a2')
    },
    {
      question: t('etfFlows.faq.q3'),
      answer: t('etfFlows.faq.a3')
    },
    {
      question: t('etfFlows.faq.q4'),
      answer: t('etfFlows.faq.a4')
    },
    {
      question: t('etfFlows.faq.q5'),
      answer: t('etfFlows.faq.a5')
    }
  ], [t]);

  // Add FAQ Schema
  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    let faqScript = document.querySelector('script[data-schema="etf-faq"]');
    if (!faqScript) {
      faqScript = document.createElement('script');
      faqScript.setAttribute('type', 'application/ld+json');
      faqScript.setAttribute('data-schema', 'etf-faq');
      document.head.appendChild(faqScript);
    }
    faqScript.textContent = JSON.stringify(faqSchema);

    return () => {
      const script = document.querySelector('script[data-schema="etf-faq"]');
      if (script) script.remove();
    };
  }, []);

  if (loading) {
    return (
      <div className="etf-page">
        <SEO page="etfFlows" />
        <Header />
        <div className="etf-container">
          <div className="etf-loading">{t('etfFlows.loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="etf-page">
        <SEO page="etfFlows" />
        <Header />
        <div className="etf-container">
          <div className="etf-error">{t('etfFlows.error')}: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="etf-page">
      <SEO page="etfFlows" />
      <Header />

      <div className="etf-container">
        <div className="etf-header">
          <h1 className="etf-header-title">{t('etfFlows.title')}</h1>
          <p className="etf-header-subtitle">{t('etfFlows.subtitle')}</p>
        </div>

        {/* Stats Cards */}
        <div className="etf-stats-grid">
          <div className="etf-stat-card">
            <div className="etf-stat-label">{t('etfFlows.btcLabel')}</div>
            <div className={`etf-stat-value ${isPositive(today.BTC?.netFlow) ? 'positive' : 'negative'}`}>
              {formatFlow(today.BTC?.netFlow)}
            </div>
            <div className="etf-stat-date">{formatFullDate(today.BTC?.date)}</div>
          </div>

          <div className="etf-stat-card">
            <div className="etf-stat-label">{t('etfFlows.ethLabel')}</div>
            <div className={`etf-stat-value ${isPositive(today.ETH?.netFlow) ? 'positive' : 'negative'}`}>
              {formatFlow(today.ETH?.netFlow)}
            </div>
            <div className="etf-stat-date">{formatFullDate(today.ETH?.date)}</div>
          </div>

          <div className="etf-stat-card">
            <div className="etf-stat-label">{t('etfFlows.solLabel')}</div>
            <div className={`etf-stat-value ${isPositive(today.SOL?.netFlow) ? 'positive' : 'negative'}`}>
              {formatFlow(today.SOL?.netFlow)}
            </div>
            <div className="etf-stat-date">{formatFullDate(today.SOL?.date)}</div>
          </div>
        </div>

        {/* Chart Card */}
        <div className="etf-chart-card">
          <div className="etf-chart-header">
            <h2 className="etf-chart-title">{t('etfFlows.chartTitle')}</h2>
            <div className="etf-date-selector">
              {dateOptions.map((item) => (
                <button
                  key={item.date}
                  className={`etf-date-btn ${selectedDate === item.date ? 'active' : ''}`}
                  onClick={() => {
                    trackFilterChange('etf_date', item.date, { page: '/etf-flows' });
                    setSelectedDate(item.date);
                  }}
                >
                  {formatDate(item.date)}
                </button>
              ))}
            </div>
          </div>

          <div className="etf-chart-container">
            {/* BTC Bar */}
            <div className="etf-bar-wrapper">
              <div className="etf-bar-container">
                <div
                  className={`etf-bar ${isPositive(selectedData?.BTC) ? 'bar-positive' : 'bar-negative'}`}
                  style={{ height: `${barHeights.BTC}%` }}
                >
                  <div className={`etf-bar-value ${isPositive(selectedData?.BTC) ? 'positive' : 'negative'}`}>
                    {formatFlow(selectedData?.BTC)}
                  </div>
                </div>
              </div>
              <div className="etf-bar-label">BTC</div>
              <div className="etf-bar-sublabel">{t('etfFlows.btcName')}</div>
            </div>

            {/* ETH Bar */}
            <div className="etf-bar-wrapper">
              <div className="etf-bar-container">
                <div
                  className={`etf-bar ${isPositive(selectedData?.ETH) ? 'bar-positive' : 'bar-negative'}`}
                  style={{ height: `${barHeights.ETH}%` }}
                >
                  <div className={`etf-bar-value ${isPositive(selectedData?.ETH) ? 'positive' : 'negative'}`}>
                    {formatFlow(selectedData?.ETH)}
                  </div>
                </div>
              </div>
              <div className="etf-bar-label">ETH</div>
              <div className="etf-bar-sublabel">{t('etfFlows.ethName')}</div>
            </div>

            {/* SOL Bar */}
            <div className="etf-bar-wrapper">
              <div className="etf-bar-container">
                <div
                  className={`etf-bar ${isPositive(selectedData?.SOL) ? 'bar-positive' : 'bar-negative'}`}
                  style={{ height: `${barHeights.SOL}%` }}
                >
                  <div className={`etf-bar-value ${isPositive(selectedData?.SOL) ? 'positive' : 'negative'}`}>
                    {formatFlow(selectedData?.SOL)}
                  </div>
                </div>
              </div>
              <div className="etf-bar-label">SOL</div>
              <div className="etf-bar-sublabel">{t('etfFlows.solName')}</div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="etf-info-section">
          <div className="etf-info-grid">
            <div className="etf-info-block">
              <h3 className="etf-info-block-title">{t('etfFlows.infoTitle1')}</h3>
              <p className="etf-info-block-content">
                {t('etfFlows.infoContent1')}
              </p>
            </div>

            <div className="etf-info-block">
              <h3 className="etf-info-block-title">{t('etfFlows.infoTitle2')}</h3>
              <p className="etf-info-block-content">
                {t('etfFlows.infoContent2')}
              </p>
            </div>

            <div className="etf-info-block">
              <h3 className="etf-info-block-title">{t('etfFlows.infoTitle3')}</h3>
              <p className="etf-info-block-content">
                {t('etfFlows.infoContent3')}
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="etf-faq-section">
          <h2 className="etf-faq-title">{t('etfFlows.faqTitle')}</h2>

          {faqData.map((faq, index) => (
            <div
              key={index}
              className={`etf-faq-item ${expandedFaq === index ? 'active' : ''}`}
            >
              <button
                className="etf-faq-button"
                onClick={() => {
                  if (expandedFaq !== index) {
                    trackFeatureUsage('etf_faq_expand', { question_index: index, page: '/etf-flows' });
                  }
                  setExpandedFaq(expandedFaq === index ? null : index);
                }}
              >
                <h4 className="etf-faq-question">{faq.question}</h4>
                <svg
                  className="etf-faq-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="etf-faq-answer-container">
                <p className="etf-faq-answer">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default EtfFlowPage;
